import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { logger } from '@havenengineering/module-haven-logging';
import { CreateRefundResponse } from '@havenengineering/module-owners-freshdesk';
import { LoadingIndicator } from '@havenengineering/module-shared-library/dist/components/LoadingIndicator/LoadingIndicator';
import { NumberedStepper } from '@havenengineering/module-shared-owners-ui/dist/components/NumberedStepper/NumberedStepper';
import { SectionHeader } from '@havenengineering/module-shared-owners-ui/dist/components/SectionHeader/SectionHeader';
import { useAuthContext } from '@havenengineering/module-shared-owners-ui/dist/contexts/auth';
import { DateTime } from 'luxon';

import { BackButton } from '../../components/BackButton/BackButton';
import { Account } from '../../components/RequestFoundStepper/steps/Account';
import { Covid19Disclaimer } from '../../components/RequestFoundStepper/steps/Covid19Disclaimer';
import { Refund } from '../../components/RequestFoundStepper/steps/Refund';
import { RefundChoice } from '../../components/RequestFoundStepper/steps/RefundChoice';
import { SecurityQuestions } from '../../components/RequestFoundStepper/steps/SecurityQuestions';
import { SummaryRefund } from '../../components/RequestFoundStepper/steps/SummaryRefund';
import { SummaryRequest } from '../../components/RequestFoundStepper/steps/SummaryRequest';
import { withApiBaseUrl, withBFFUrl } from '../../helpers/fetch';
import { fetchWrapper } from '../../helpers/fetch';
import { pageView, refundConfirmation } from '../../helpers/googleTag';
import { getRefundType } from '../../helpers/refund';
import { fetchSiteFeeHolding } from '../../helpers/siteFee';
import styles from '../../styles/pages/request-funds.module.scss';
import { RefundForm, Security } from '../../types/refund';

const steps = [
  'start-application',
  'credit-balance',
  'account-details',
  'security-questions',
  'bank-details',
  'summary',
] as const;

const currentYear = DateTime.now().year;

const RequestRefund = () => {
  const router = useRouter();
  const { loggedInUser, activeAccount, gtmPageViewAuthInfo } = useAuthContext();
  const isUserLoggedIn = !!loggedInUser;
  const { type } = router.query;

  const isLettingIncomeRefund = type === 'letting-income';
  const refundType = isLettingIncomeRefund ? 'letting-income' : 'site-fees';

  const [activeStep, setActiveStep] = useState(
    isLettingIncomeRefund ? (isUserLoggedIn ? 4 : 2) : 0
  );
  const [previousStep, setPreviousStep] = useState<number[]>([]);
  const [balance, setBalance] = useState<number>(0);

  const getBalance = useCallback(async () => {
    try {
      if (isLettingIncomeRefund && activeAccount && isUserLoggedIn) {
        const response = await fetchWrapper(
          withApiBaseUrl(
            `/ledger/letting?accountId=${activeAccount?.accountID}&year=${currentYear}`
          ),
          {
            method: 'GET',
            credentials: 'include',
          }
        );
        return setBalance(response?.netLettingIncomeDueToOrFromOwner);
      }

      if (isUserLoggedIn) {
        const response = await fetchWrapper(
          withApiBaseUrl(
            `/ledger/balance?accountId=${activeAccount?.accountID}`
          ),
          {
            method: 'GET',
            credentials: 'include',
          }
        );
        return setBalance(response.balance);
      }
    } catch (error) {
      console.error(error);
    }
  }, [activeAccount, isLettingIncomeRefund, isUserLoggedIn]);

  useEffect(() => {
    if (activeAccount) {
      getBalance();
    }
  }, [activeAccount, getBalance]);

  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isLocalLoading, setLocalLoading] = useState(false);
  const [showBack, setShowBack] = useState(false);

  const [formState, setFormState] = useState<RefundForm>({
    accountNumber: '',
    email: loggedInUser?.email || '',
    mobile: '',
    fullName: loggedInUser?.name || '',
    bankAccountNo: '',
    sortCode: '',
    amount: '',
    totalAmount: balance?.toString() || '',
    security: {} as Security,
  });

  const getAnalyticsData = useCallback(
    (): RefundData => ({
      ownerAccount: activeAccount
        ? activeAccount.accountNo
        : formState.accountNumber,
      refundType,
      siteFeeBalance: Number.parseFloat(formState.totalAmount).toFixed(2),
      refundAmount: Number.parseFloat(formState.amount).toFixed(2),
      requiresCallback: Number(formState.totalAmount) >= 100,
    }),
    [formState, refundType, activeAccount]
  );

  useEffect(() => {
    if (activeAccount) {
      setFormState((prevState) => ({
        ...prevState,
        totalAmount: balance.toString(),
      }));
    }
  }, [activeAccount, balance]);

  useEffect(() => {
    pageView(
      getRefundType(isLettingIncomeRefund),
      `/ga-virtual/owners-account/${getRefundType(
        isLettingIncomeRefund,
        '-'
      )}/${steps[activeStep]}`,
      'owners account',
      gtmPageViewAuthInfo
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStep, isLettingIncomeRefund]);

  const handleStepChange = (changedForm?: Partial<RefundForm>) => {
    setLocalLoading(true);
    if (changedForm) {
      setFormState((prevState) => ({
        ...prevState,
        ...changedForm,
      }));
    }
    setShowBack(true);
    setTimeout(() => {
      if (isUserLoggedIn && activeStep === 1) {
        setActiveStep(activeStep + 3);
      } else {
        setActiveStep(activeStep + 1);
      }
      setPreviousStep((state) => [...state, activeStep]);
      setLocalLoading(false);
    }, 1000);
  };

  const handleStepBackChange = () => {
    setLocalLoading(true);
    setTimeout(() => {
      setActiveStep(previousStep[previousStep.length - 1]);
      setPreviousStep((state) => {
        const newState = state.filter(
          (element, index) => index < state.length - 1
        );
        if (newState.length === 0) {
          setShowBack(false);
        }
        return newState;
      });
      setLocalLoading(false);
    }, 1000);
  };

  const handleReturnToDashboard = () => {
    router.push('/');
  };

  const handleSubmit = async () => {
    setLoading(true);
    const { totalAmount } = formState;

    const body = {
      ...formState,
      accountNumber: activeAccount
        ? activeAccount.accountNo
        : formState.accountNumber,
      userType: loggedInUser?.userType || '',
      refundType,
      ...(Number(totalAmount) >= 100 ? { tags: ['call_back'] } : {}),
    };

    try {
      const { result, error } = await fetchWrapper<CreateRefundResponse>(
        withBFFUrl('/freshdesk/refunds'),
        {
          method: 'POST',
          body: JSON.stringify(body),
          credentials: 'include',
        }
      );
      if (error) throw new Error(error.message);
      await refundConfirmation(
        { ...getAnalyticsData(), ...result },
        {
          successFlag: 'success',
        }
      );
      router.push(
        refundType === 'letting-income'
          ? '/request-funds/success?type=letting-income'
          : Number(formState.totalAmount) >= 100
          ? '/request-funds/success-callback'
          : '/request-funds/success'
      );
      window.scrollTo({ left: 0, top: 0 });
    } catch (error) {
      setError(true);
      await refundConfirmation(getAnalyticsData(), {
        successFlag: 'error',
        errorType: error.message.toLowerCase(),
      });
    } finally {
      setLoading(false);
    }
  };

  const getActiveStep = (currentStep: number) => {
    const step = steps[currentStep];
    switch (step) {
      case 'start-application':
        return (
          <Covid19Disclaimer
            handleReturnToDashboard={handleReturnToDashboard}
            handleStepChange={handleStepChange}
          />
        );
      case 'credit-balance':
        return (
          <RefundChoice
            handleReturnToDashboard={handleReturnToDashboard}
            handleStepChange={handleStepChange}
          />
        );
      case 'account-details':
        return (
          <Account state={formState} handleStepChange={handleStepChange} />
        );
      case 'security-questions':
        return (
          <SecurityQuestions
            state={formState}
            handleStepChange={handleStepChange}
          />
        );
      case 'bank-details':
        return <Refund state={formState} handleStepChange={handleStepChange} />;
      case 'summary':
        if (Number(formState.totalAmount) >= 100) {
          return (
            <SummaryRequest
              state={formState}
              handleSubmit={handleSubmit}
              handleReturnToDashboard={handleReturnToDashboard}
              isError={error}
              isLoading={loading}
            />
          );
        }

        return (
          <SummaryRefund
            isError={error}
            isLoading={loading}
            state={formState}
            handleSubmit={handleSubmit}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div>
      <SectionHeader
        title={isLettingIncomeRefund ? 'Haven Letting' : 'Credit application'}
      />
      {isLettingIncomeRefund &&
        (activeStep === 2 || (isUserLoggedIn && activeStep === 4)) && (
          <p className="owners-body" data-testid="letting-income-intro">
            If you have letting income on your account, fill in this form to
            request the income as a BACS transfer into your bank account. This
            will be reviewed by the team and if you’re eligible, the money with
            be transferred to your nominated bank account 28 working days from
            processing.
          </p>
        )}
      <div className={styles.innerContainer}>
        {isLocalLoading && (
          <div className={styles.extraMarginTop}>
            <LoadingIndicator loading={true} />
          </div>
        )}
        {!isLocalLoading && showBack && activeStep < 5 && (
          <div className={styles.backContainer}>
            <BackButton handleClick={handleStepBackChange} />
          </div>
        )}
        <div className={styles.card}>
          {!isUserLoggedIn &&
            activeStep > 1 &&
            activeStep < 5 &&
            !isLocalLoading && (
              <NumberedStepper steps={3} activeStep={activeStep - 3} />
            )}
          {!isLocalLoading && (
            <div className={styles.content}>{getActiveStep(activeStep)}</div>
          )}
        </div>
      </div>
    </div>
  );
};

export const getStaticProps = async () => {
  try {
    const isSiteFeeHolding = await fetchSiteFeeHolding();
    return {
      props: {
        pageTitle: 'Request refunds',
        isSiteFeeHolding,
        availableForGuests: true,
      },
      revalidate: 1,
    };
  } catch (error) {
    logger.error(error);
    return {
      props: {
        error: true,
      },
    };
  }
};

export default RequestRefund;
